import React, { useState } from "react";
import { useQuery, useMutation } from "convex/react";
import { api } from "../../../../../convex/_generated/api";
import { Id } from "../../../../../convex/_generated/dataModel";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/components/ui/use-toast";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Briefcase, Plus, Edit, Trash2, Check, X, AlertCircle, Eye } from "lucide-react";
import { cn } from "@/lib/utils";
import { EmptyState } from "@/components/ui/empty-state";

interface PositionFormData {
  name: string;
  description: string;
  enabled: boolean;
}

export function PositionsTab() {
  const { toast } = useToast();
  const positions = useQuery(api.positions.list);
  const createPosition = useMutation(api.positions.create);
  const updatePosition = useMutation(api.positions.update);
  const deletePosition = useMutation(api.positions.remove);
  const toggleEnabled = useMutation(api.positions.toggleEnabled);

  const [isCreating, setIsCreating] = useState(false);
  const [editingId, setEditingId] = useState<Id<"positions"> | null>(null);
  const [viewingPosition, setViewingPosition] = useState<any | null>(null);
  const [formData, setFormData] = useState<PositionFormData>({
    name: "",
    description: "",
    enabled: true,
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  const resetForm = () => {
    setFormData({
      name: "",
      description: "",
      enabled: true,
    });
    setErrors({});
    setIsCreating(false);
    setEditingId(null);
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = "Position name is required";
    }

    if (!formData.description.trim()) {
      newErrors.description = "Position description is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleCreate = async () => {
    if (!validateForm()) return;

    try {
      await createPosition({
        name: formData.name,
        description: formData.description,
        enabled: formData.enabled,
      });

      toast({
        title: "Success",
        description: "Position created successfully",
        duration: 5000,
      });

      resetForm();
    } catch (error) {
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to create position",
        variant: "destructive",
        duration: 5000,
      });
    }
  };

  const handleUpdate = async () => {
    if (!editingId || !validateForm()) return;

    try {
      await updatePosition({
        id: editingId,
        name: formData.name,
        description: formData.description,
        enabled: formData.enabled,
      });

      toast({
        title: "Success",
        description: "Position updated successfully",
        duration: 5000,
      });

      resetForm();
    } catch (error) {
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to update position",
        variant: "destructive",
        duration: 5000,
      });
    }
  };

  const handleDelete = async (id: Id<"positions">) => {
    if (!confirm("Are you sure you want to delete this position?")) return;

    try {
      await deletePosition({ id });

      toast({
        title: "Success",
        description: "Position deleted successfully",
        duration: 5000,
      });
    } catch (error) {
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to delete position",
        variant: "destructive",
        duration: 5000,
      });
    }
  };

  const handleToggleEnabled = async (id: Id<"positions">) => {
    try {
      await toggleEnabled({ id });

      toast({
        title: "Success",
        description: "Position status updated successfully",
        duration: 5000,
      });
    } catch (error) {
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to update position status",
        variant: "destructive",
        duration: 5000,
      });
    }
  };

  const startEdit = (position: any) => {
    setFormData({
      name: position.name,
      description: position.description,
      enabled: position.enabled,
    });
    setEditingId(position._id);
    setIsCreating(false);
  };

  const startCreate = () => {
    resetForm();
    setIsCreating(true);
  };

  return (
    <div className="space-y-6">
      {/* Header and Form Card */}
      <Card className="p-6">
        <div className="space-y-6">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="rounded-lg bg-blue-100 p-2">
                <Briefcase className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-gray-900">Employee Positions</h2>
                <p className="text-sm text-gray-500">
                  Manage job positions that can be assigned to employees
                </p>
              </div>
            </div>
            {!isCreating && !editingId && (
              <Button onClick={startCreate} className="gap-2">
                <Plus className="h-4 w-4" />
                Add Position
              </Button>
            )}
          </div>

          {/* Form */}
          {(isCreating || editingId) && (
            <div className="space-y-4 border-t border-gray-200 pt-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium text-gray-900">
                  {editingId ? "Edit Position" : "Create New Position"}
                </h3>
                <Button variant="ghost" size="sm" onClick={resetForm} className="gap-2">
                  <X className="h-4 w-4" />
                  Cancel
                </Button>
              </div>

              <div className="grid gap-4">
                {/* Position Name */}
                <div className="space-y-2">
                  <Label htmlFor="name">
                    Position Name <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="name"
                    placeholder="e.g., Cleaner, Supervisor, Manager"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className={cn(errors.name && "border-red-500")}
                  />
                  {errors.name && (
                    <p className="flex items-center gap-1 text-sm text-red-500">
                      <AlertCircle className="h-3 w-3" />
                      {errors.name}
                    </p>
                  )}
                </div>

                {/* Position Description */}
                <div className="space-y-2">
                  <Label htmlFor="description">
                    Description <span className="text-red-500">*</span>
                  </Label>
                  <Textarea
                    id="description"
                    placeholder="Describe the position responsibilities and duties..."
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows={4}
                    className={cn(errors.description && "border-red-500")}
                  />
                  {errors.description && (
                    <p className="flex items-center gap-1 text-sm text-red-500">
                      <AlertCircle className="h-3 w-3" />
                      {errors.description}
                    </p>
                  )}
                </div>

                {/* Enabled Status */}
                <div className="flex items-center justify-between rounded-lg bg-gray-50 p-4">
                  <div className="space-y-0.5">
                    <Label htmlFor="enabled" className="text-base">
                      Enabled
                    </Label>
                    <p className="text-sm text-gray-500">
                      Make this position available for assignment
                    </p>
                  </div>
                  <Switch
                    id="enabled"
                    checked={formData.enabled}
                    onCheckedChange={(checked) => setFormData({ ...formData, enabled: checked })}
                  />
                </div>

                {/* Action Buttons */}
                <div className="flex justify-end gap-2 pt-2">
                  <Button variant="outline" onClick={resetForm}>
                    Cancel
                  </Button>
                  <Button onClick={editingId ? handleUpdate : handleCreate} className="gap-2">
                    <Check className="h-4 w-4" />
                    {editingId ? "Update Position" : "Create Position"}
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      </Card>

      {/* Positions List Card */}
      <Card className="p-6">
        <div className="space-y-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">All Positions</h3>
            <p className="text-sm text-gray-500">{positions?.length || 0} position(s) configured</p>
          </div>

          {positions === undefined ? (
            <div className="py-8 text-center text-gray-500">Loading positions...</div>
          ) : positions.length === 0 ? (
            <EmptyState
              icon={Briefcase}
              title="No positions configured yet"
              description="Click 'Add Position' to create your first position."
            />
          ) : (
            <div className="overflow-hidden rounded-lg border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {positions.map((position: any) => (
                    <TableRow key={position._id}>
                      <TableCell className="font-medium">{position.name}</TableCell>
                      <TableCell className="max-w-md">
                        <p className="line-clamp-2 text-sm text-gray-600">{position.description}</p>
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant={position.enabled ? "default" : "secondary"}
                          className={cn(
                            position.enabled
                              ? "bg-green-100 text-green-800 hover:bg-green-200"
                              : "bg-gray-100 text-gray-800"
                          )}
                        >
                          {position.enabled ? "Enabled" : "Disabled"}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleToggleEnabled(position._id)}
                          >
                            {position.enabled ? "Disable" : "Enable"}
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setViewingPosition(position)}
                            title="View details"
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => startEdit(position)}>
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDelete(position._id)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </div>
      </Card>

      {/* View Position Dialog */}
      <Dialog open={!!viewingPosition} onOpenChange={(open) => !open && setViewingPosition(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Briefcase className="h-5 w-5 text-blue-600" />
              {viewingPosition?.name}
            </DialogTitle>
            <DialogDescription>View position details and responsibilities</DialogDescription>
          </DialogHeader>

          {viewingPosition && (
            <div className="mt-4 space-y-4">
              {/* Position Name */}
              <div className="space-y-2">
                <Label className="text-sm font-medium text-gray-700">Position Name</Label>
                <div className="rounded-md border border-gray-200 bg-gray-50 p-3">
                  <p className="text-sm text-gray-900">{viewingPosition.name}</p>
                </div>
              </div>

              {/* Position Description */}
              <div className="flex-1 space-y-2">
                <Label className="text-sm font-medium text-gray-700">Description</Label>
                <div className="max-h-[300px] overflow-y-auto rounded-md border border-gray-200 bg-gray-50 p-3">
                  <p className="whitespace-pre-wrap text-sm text-gray-900">
                    {viewingPosition.description}
                  </p>
                </div>
              </div>

              {/* Metadata */}
              <div className="border-t border-gray-200 pt-4">
                <div className="grid grid-cols-3 gap-4 text-sm">
                  <div>
                    <Label className="text-xs text-gray-500">Status</Label>
                    <div className="mt-1">
                      <Badge
                        variant={viewingPosition.enabled ? "default" : "secondary"}
                        className={cn(
                          viewingPosition.enabled
                            ? "bg-green-100 text-green-800"
                            : "bg-gray-100 text-gray-800"
                        )}
                      >
                        {viewingPosition.enabled ? "Enabled" : "Disabled"}
                      </Badge>
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs text-gray-500">Created</Label>
                    <p className="mt-1 text-gray-900">
                      {new Date(viewingPosition.createdAt).toLocaleDateString()}
                    </p>
                  </div>
                  <div>
                    <Label className="text-xs text-gray-500">Last Updated</Label>
                    <p className="mt-1 text-gray-900">
                      {new Date(viewingPosition.updatedAt).toLocaleDateString()}
                    </p>
                  </div>
                </div>
              </div>

              {/* Close Button */}
              <div className="flex justify-end pt-4">
                <Button onClick={() => setViewingPosition(null)} variant="outline">
                  Close
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
